import { createInertiaApp, router } from '@inertiajs/react'; // Add router import
import { createElement, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { PageProps } from '@/types';
import Layout from '../components/Layout';
import { UserProvider } from '../src/context/UserContext';
import { setupMetaUpdater } from '../components/MetaTagUpdater';

type PageComponent<P = {}> = React.FC<P> & {
  layout?: (page: ReactNode) => ReactNode;
  useBlankLayout?: boolean;
};

// Configure Inertia router defaults - this will ensure preserveUrl is false by default
router.on('before', (event) => {
  // Set preserveUrl to false for all navigation events
  event.detail.visit.preserveUrl = false;
  console.log('🔄 Inertia navigation with preserveUrl=false');
});

// Initialize the meta tag updater
setupMetaUpdater();

// The success listener in the MetaTagUpdater will replace this one,
// but we'll keep it for now for debugging purposes
router.on('success', () => {
  console.log('✅ Navigation successful, URL:', window.location.href);
  // Force update browser history state to fix browser share functionality
  history.replaceState({ ...history.state }, document.title, window.location.href);
});

const app = document.getElementById('app');
if (app) {
  console.log('✅ Inertia mounting on #app...');
  createInertiaApp({
    resolve: (name) => {
      const pages = import.meta.glob<{
        default: PageComponent<PageProps>;
      }>('../pages/**/*.tsx', { eager: true });
      const importedPage = pages[`../pages/${name}.tsx`];
      if (!importedPage) {
        console.error(`❌ Missing Inertia page component: '${name}.tsx'`);
        throw new Error(`Missing Inertia page: ${name}`);
      }
      const Page = importedPage.default as PageComponent<PageProps>;
      // No need to check for ERB pages anymore
      if (!Page.layout) {
        console.log('Applying default layout to page:', name);
        Page.layout = (page) => {
          console.log('Layout function executing for page:', name);
          return createElement(Layout, { children: page });
        };
      } else {
        console.log('Page already has a layout:', name);
      }
      return Page;
    },
    setup({ el, App, props }) {
      createRoot(el).render(
        createElement(UserProvider, {
          children: createElement(App, props),
        }),
      );
    },
  });
}
